import { Link } from '@/lib/router-compat';
import { Phone, Mail, MapPin, Clock, Lock, ShieldCheck } from 'lucide-react';
import { COMPANY } from '@/config/company';
import logo from '@/assets/quick-credit-logo.png';

const productLinks = [
  { label: 'Personal Loans', to: '/services' },
  { label: 'Debt Consolidation', to: '/services' },
  { label: 'Home Improvement Loans', to: '/services' },
  { label: 'Loan Calculator', to: '/#calculator' },
];

const companyLinks = [
  { label: 'About Us', to: '/about' },
  { label: 'How It Works', to: '/#how-it-works' },
  { label: 'Eligibility', to: '/#eligibility' },
  { label: 'Contact Us', to: '/contact' },
];

const legalLinks = [
  { label: 'Privacy Policy', to: '/privacy-policy' },
  { label: 'Terms & Conditions', to: '/terms' },
  { label: 'Cookie Policy', to: '/cookie-policy' },
  { label: 'Responsible Lending Policy', to: '/responsible-lending' },
  { label: 'Complaints Procedure', to: '/complaints' },
];

export default function Footer() {
  return (
    <footer className="bg-primary text-primary-foreground pt-16 pb-10">
      <div className="container mx-auto px-6">
        <div className="grid md:grid-cols-2 lg:grid-cols-4 gap-10 mb-12">
          {/* Brand + contact */}
          <div>
            <Link to="/" className="flex items-center gap-2 mb-4">
              <img src={logo} alt="Quick Credit logo" className="h-8 w-8 rounded-lg" width={32} height={32} loading="lazy" />
              <span className="font-display font-bold text-lg">Quick Credit</span>
            </Link>
            <p className="text-primary-foreground/70 text-sm mb-5">
              Simple, transparent lending. Clear rates, honest terms and responsible credit decisions.
            </p>
            <ul className="space-y-3 text-sm text-primary-foreground/80">
              <li className="flex gap-3">
                <MapPin className="h-4 w-4 flex-shrink-0 mt-0.5" />
                <span>{COMPANY.addressLine1}<br />{COMPANY.addressLine2}</span>
              </li>
              <li className="flex gap-3">
                <Phone className="h-4 w-4 flex-shrink-0 mt-0.5" />
                <a href={COMPANY.phoneHref} className="hover:text-primary-foreground transition-colors">{COMPANY.phone}</a>
              </li>
              <li className="flex gap-3">
                <Mail className="h-4 w-4 flex-shrink-0 mt-0.5" />
                <a href={`mailto:${COMPANY.email}`} className="hover:text-primary-foreground transition-colors">{COMPANY.email}</a>
              </li>
              <li className="flex gap-3">
                <Clock className="h-4 w-4 flex-shrink-0 mt-0.5" />
                <span>
                  {COMPANY.hours.map((h) => (
                    <span key={h.days} className="block">{h.days}: {h.time}</span>
                  ))}
                </span>
              </li>
            </ul>
          </div>

          <div>
            <h3 className="font-semibold mb-4">Products</h3>
            <ul className="space-y-2 text-sm text-primary-foreground/70">
              {productLinks.map((l) => (
                <li key={l.label}>
                  <Link to={l.to} className="hover:text-primary-foreground transition-colors">{l.label}</Link>
                </li>
              ))}
            </ul>
          </div>

          <div>
            <h3 className="font-semibold mb-4">Company</h3>
            <ul className="space-y-2 text-sm text-primary-foreground/70">
              {companyLinks.map((l) => (
                <li key={l.label}>
                  <Link to={l.to} className="hover:text-primary-foreground transition-colors">{l.label}</Link>
                </li>
              ))}
            </ul>
          </div>

          <div>
            <h3 className="font-semibold mb-4">Legal</h3>
            <ul className="space-y-2 text-sm text-primary-foreground/70">
              {legalLinks.map((l) => (
                <li key={l.label}>
                  <Link to={l.to} className="hover:text-primary-foreground transition-colors">{l.label}</Link>
                </li>
              ))}
            </ul>
          </div>
        </div>

        {/* Trust strip */}
        <div className="flex flex-wrap items-center justify-center gap-6 py-6 border-t border-primary-foreground/10 text-xs text-primary-foreground/70">
          <span className="flex items-center gap-2"><Lock className="h-4 w-4" /> 256-bit SSL secured</span>
          <span className="flex items-center gap-2"><ShieldCheck className="h-4 w-4" /> Responsible lending</span>
          <span className="flex items-center gap-2">Transparent pricing — no hidden fees</span>
        </div>

        <div className="pt-6 border-t border-primary-foreground/10 space-y-4">
          <p className="text-xs text-primary-foreground/60 leading-relaxed">
            Representative example: borrow $10,000 over 36 months at 18.9% APR (fixed). Monthly repayment
            $365.66. Total interest $3,163.76. Total amount repayable $13,163.76. APRs range from 5.96% to
            35.99% and depend on your credit profile, loan amount and term. All loans are subject to
            identity, credit and affordability checks; approval is not guaranteed. Late or missed payments
            may incur fees and affect your credit rating.
          </p>
          <p className="text-sm text-primary-foreground/70">
            © {new Date().getFullYear()} {COMPANY.legalName}. All rights reserved.
          </p>
        </div>
      </div>
    </footer>
  );
}
